import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Switch } from '../components/ui/switch';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Building2, ClipboardList, AlertTriangle, CheckCircle2, FileCheck, Briefcase,
  DollarSign, Wrench, CalendarClock, Shield, TrendingUp, TrendingDown,
  Clock, AlertCircle, ChevronRight, RefreshCw, Loader2, Filter, X,
  BarChart3, PieChart, Activity, Users, ArrowLeft, HelpCircle, FileDown
} from 'lucide-react';
import { StatCard, StatCardGrid } from '../components/StatCard';
import EnergyVerdictPill from '../components/EnergyVerdictPill';
// v1.3.37.63.1 — SecurityAtAGlanceTile relocated to System Admin →
// Audit Log tab; import kept as a comment for archaeology.
// import { SecurityAtAGlanceTile } from '../components/SecurityAtAGlanceTile';
import { toast } from 'sonner';
import { downloadFile } from '../lib/download';

// Simple chart components (no external library needed)
const HorizontalBar = ({ label, value, maxValue, color, onClick, prefix = '' }) => {
  const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
  return (
    <div 
      className={`mb-2 ${onClick ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 rounded p-1 -m-1' : ''}`}
      onClick={onClick}
    >
      <div className="flex justify-between text-sm mb-1">
        <span className="text-slate-600 dark:text-slate-300">{label}</span>
        <span className="font-semibold text-slate-900 dark:text-white">{prefix}{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const TrendLine = ({ data, color = 'text-teal-600', height = 40 }) => {
  if (!data || data.length === 0) return <span className="text-slate-400 text-xs">No data</span>;
  
  const max = Math.max(...data.map(d => d.count), 1);
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1 || 1)) * 100;
    const y = height - (d.count / max) * height;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox={`0 0 100 ${height}`} className="w-full" style={{ height }}>
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={color}
      />
    </svg>
  );
};

// Overlay chart for the Work Order Trends card: created + completed plotted on
// the SAME axes with a shaded gap region — green where completed >= created
// (winning), red where created > completed (losing).
const MONTH_ABBREV = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const formatMonthLabel = (label) => {
  if (!label) return '';
  // Expecting "YYYY-MM" — fall back to first 3 chars otherwise
  const m = /^(\d{4})-(\d{2})/.exec(label);
  if (!m) return String(label).slice(0, 3);
  const monthIdx = parseInt(m[2], 10) - 1;
  return `${MONTH_ABBREV[monthIdx] || ''} '${m[1].slice(2)}`;
};
const WorkOrderTrendOverlay = ({ created, completed, height = 80 }) => {
  if (!created?.length || !completed?.length) {
    return <div className="text-center text-slate-400 text-xs py-6">No trend data available</div>;
  }
  const n = Math.min(created.length, completed.length);
  const series = Array.from({ length: n }, (_, i) => ({
    label: created[i]?.month || completed[i]?.month || '',
    created: created[i]?.count ?? 0,
    completed: completed[i]?.count ?? 0,
  }));
  const max = Math.max(1, ...series.map(s => Math.max(s.created, s.completed)));
  const w = 200; // viewBox width
  const xAt = (i) => (i / (n - 1 || 1)) * w;
  const yAt = (v) => height - (v / max) * height;
  
  const createdPath = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${yAt(s.created)}`).join(' ');
  const completedPath = series.map((s, i) => `${i === 0 ? 'M' : 'L'}${xAt(i)},${yAt(s.completed)}`).join(' ');
  
  // Build per-segment shaded regions so each segment gets the correct color
  // (red when created>completed, green when completed>=created).
  const segments = [];
  for (let i = 0; i < n - 1; i++) {
    const a = series[i];
    const b = series[i + 1];
    const losing = a.created > a.completed || b.created > b.completed;
    segments.push({
      key: i,
      path: `M${xAt(i)},${yAt(a.created)} L${xAt(i + 1)},${yAt(b.created)} L${xAt(i + 1)},${yAt(b.completed)} L${xAt(i)},${yAt(a.completed)} Z`,
      fill: losing ? 'rgba(239, 68, 68, 0.18)' : 'rgba(16, 185, 129, 0.18)',
    });
  }
  
  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none">
        {/* Shaded gap regions */}
        {segments.map(s => (
          <path key={s.key} d={s.path} fill={s.fill} stroke="none" />
        ))}
        {/* Created line — blue */}
        <path d={createdPath} fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {/* Completed line — green */}
        <path d={completedPath} fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {/* Endpoint dots */}
        <circle cx={xAt(n - 1)} cy={yAt(series[n - 1].created)} r="2.5" fill="#0ea5e9" />
        <circle cx={xAt(n - 1)} cy={yAt(series[n - 1].completed)} r="2.5" fill="#10b981" />
      </svg>
      {/* Month axis labels rendered OUTSIDE the SVG so long names don't get stretched */}
      {n > 1 && (
        <div className="flex justify-between mt-1 text-[10px] text-slate-400 dark:text-slate-500">
          <span>{formatMonthLabel(series[0].label)}</span>
          <span>{formatMonthLabel(series[n - 1].label)}</span>
        </div>
      )}
    </div>
  );
};

const formatCurrency = (value) => {
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

// Shared color map for Work Order types — used by BOTH the "Work Orders by Type"
// and "Work Order Costs · Top Spend by Type" cards so the same type means the
// same color across the dashboard. Falls back to slate for unknown types.
const WO_TYPE_COLORS = {
  'Corrective (CM)': 'bg-purple-500',
  'CM': 'bg-purple-500',
  'Inspection': 'bg-blue-500',
  'Project Task': 'bg-red-500',
  'Preventive (PM)': 'bg-amber-500',
  'PM': 'bg-amber-500',
  'Emergency': 'bg-emerald-500',
  'Emergency Task': 'bg-emerald-500',
  'Other': 'bg-slate-500',
};
const woTypeColor = (label) => WO_TYPE_COLORS[label] || 'bg-slate-500';
const woTypeDisplayLabel = (type) =>
  type === 'PM' ? 'Preventive (PM)' : type === 'CM' ? 'Corrective (CM)' : type;

// SLA targets — average days-to-close goals per work order type. These are
// industry-typical defaults; future work could surface these in admin settings
// per-client. The "warn" threshold is 80% of the target so teams see amber
// BEFORE breaching. Anything above `target` renders rose with a ⚠ symbol.
const WO_SLA_TARGETS = {
  'Emergency': 1,
  'Emergency Task': 1,
  'Corrective (CM)': 7,
  'CM': 7,
  'Inspection': 14,
  'Preventive (PM)': 30,
  'PM': 30,
  'Project Task': 60,
  'Other': null,
};
const slaForType = (label) =>
  WO_SLA_TARGETS[label] !== undefined ? WO_SLA_TARGETS[label] : null;
// Returns { tone: 'ok'|'warn'|'breach', target, chipClass, symbol, tooltip }
const evaluateSla = (label, avgDays) => {
  const target = slaForType(label);
  if (target === null || target === undefined || avgDays === null || avgDays === undefined) {
    return { tone: 'ok', target: null, chipClass: 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400', symbol: null, tooltip: `Average days to close (no SLA set)` };
  }
  const warnAt = target * 0.8;
  if (avgDays > target) {
    return {
      tone: 'breach',
      target,
      chipClass: 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800',
      symbol: '⚠',
      tooltip: `Breaching SLA · target ${target}d, avg ${avgDays}d`,
    };
  }
  if (avgDays >= warnAt) {
    return {
      tone: 'warn',
      target,
      chipClass: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800',
      symbol: '•',
      tooltip: `Approaching SLA · target ${target}d, avg ${avgDays}d`,
    };
  }
  return {
    tone: 'ok',
    target,
    chipClass: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800',
    symbol: '✓',
    tooltip: `Within SLA · target ${target}d, avg ${avgDays}d`,
  };
};

// Forecast Bar Chart Component
const ForecastBarChart = ({ data, height = 200 }) => {
  if (!data || data.length === 0) {
    return <div className="text-center text-slate-400 py-8">No forecast data available</div>;
  }
  
  const maxCost = Math.max(...data.map(d => d.cost), 1);
  const currentYear = new Date().getFullYear();
  
  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[800px]" style={{ height }}>
        <div className="flex items-end justify-between h-full gap-1 px-2">
          {data.map((item, idx) => {
            const barHeight = maxCost > 0 ? (item.cost / maxCost) * (height - 40) : 0;
            const isCurrentYear = item.year === currentYear;
            const hasValue = item.cost > 0;
            
            return (
              <div key={item.year} className="flex flex-col items-center flex-1 min-w-[35px]">
                {/* Cost label */}
                <div className="text-xs text-slate-500 mb-1 h-4 flex items-end">
                  {hasValue && formatCurrency(item.cost)}
                </div>
                {/* Bar */}
                <div 
                  className={`w-full rounded-t transition-all ${
                    isCurrentYear ? 'bg-teal-500' : 
                    hasValue ? 'bg-sky-400 hover:bg-sky-500' : 'bg-slate-200 dark:bg-slate-700'
                  }`}
                  style={{ height: Math.max(barHeight, 2) }}
                  title={`${item.year}: ${formatCurrency(item.cost)}`}
                />
                {/* Year label */}
                <div className={`text-xs mt-1 ${isCurrentYear ? 'font-bold text-teal-600' : 'text-slate-400'}`}>
                  {item.year.toString().slice(-2)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// UNIFORMAT Level 2 category names — drives the "Cost by System" chart on the dashboard.
const UNIFORMAT_LABELS = {
  'A10': 'Foundations',
  'A20': 'Basement Construction',
  'B10': 'Superstructure',
  'B20': 'Exterior Enclosure',
  'B30': 'Roofing',
  'C10': 'Interior Construction',
  'C20': 'Stairs',
  'C30': 'Interior Finishes',
  'D10': 'Conveying',
  'D20': 'Plumbing',
  'D30': 'HVAC',
  'D40': 'Fire Protection',
  'D50': 'Electrical',
  'E10': 'Equipment',
  'E20': 'Furnishings',
  'F10': 'Special Construction',
  'F20': 'Selective Demolition',
  'G10': 'Site Preparation',
  'G20': 'Site Improvements',
  'G30': 'Site Mechanical Utilities',
  'G40': 'Site Electrical Utilities',
  'G90': 'Other Site Construction',
};

// Format FCI with appropriate decimal places based on value
const formatFCI = (fciPercent) => {
  if (fciPercent === 0 || fciPercent === null || fciPercent === undefined) {
    return '0.0000';
  }
  // Show 4 decimal places for values under 1%, 2 for values under 10%, 1 otherwise
  if (fciPercent < 0.01) {
    return fciPercent.toFixed(4);
  } else if (fciPercent < 1) {
    return fciPercent.toFixed(4);
  } else if (fciPercent < 10) {
    return fciPercent.toFixed(2);
  }
  return fciPercent.toFixed(1);
};

export const DashboardPage = () => {
  const { user, API, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedModule, setSelectedModule] = useState('all'); // all, fca, iwms
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('');
  // Whether financial KPIs include drafts. Default OFF (operational portfolio only).
  // Mirrors the saved user pref so the choice is sticky across sessions.
  const [includeDrafts, setIncludeDrafts] = useState(
    !!user?.preferences?.dashboard_include_drafts
  );
  const [savingIncludeDrafts, setSavingIncludeDrafts] = useState(false);
  // Window selector for the Work Order Costs card — 'ytd' or '30d'.
  const [costWindow, setCostWindow] = useState('ytd');

  const fetchStats = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (selectedClient) params.append('client_id', selectedClient);
      if (selectedFacility) params.append('facility_id', selectedFacility);
      if (includeDrafts) params.append('include_drafts', 'true');
      if (costWindow && costWindow !== 'ytd') params.append('cost_window', costWindow);
      const queryString = params.toString();
      const url = `${API}/dashboard/stats${queryString ? `?${queryString}` : ''}`;
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [API, selectedClient, selectedFacility, includeDrafts, costWindow]);

  // Persist the toggle to the user's preferences so it sticks across sessions.
  const handleToggleIncludeDrafts = async (next) => {
    setIncludeDrafts(next);
    setSavingIncludeDrafts(true);
    try {
      await axios.patch(`${API}/auth/me/preferences`, { dashboard_include_drafts: next });
    } catch (e) {
      console.error('Failed to save dashboard_include_drafts pref:', e);
    } finally {
      setSavingIncludeDrafts(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const navigateWithBack = (path, state = {}) => {
    navigate(path, { state: { ...state, fromDashboard: true } });
  };

  const [exportingPdf, setExportingPdf] = useState(false);
  const handleExportDashboardPdf = async () => {
    setExportingPdf(true);
    try {
      const params = new URLSearchParams();
      if (selectedClient) params.append('client_id', selectedClient);
      if (selectedFacility) params.append('facility_id', selectedFacility);
      const qs = params.toString();
      await downloadFile(`/dashboard/export-pdf${qs ? `?${qs}` : ''}`, token, 'FMiQ-One_Dashboard_Report.pdf');
      toast.success('Dashboard PDF saved');
    } catch (error) {
      console.error('Dashboard PDF export error:', error);
      toast.error('Failed to export dashboard PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const fciColor = stats?.financials?.fci_rating === 'Good' ? 'text-emerald-600' :
                   stats?.financials?.fci_rating === 'Fair' ? 'text-amber-500' :
                   stats?.financials?.fci_rating === 'Poor' ? 'text-orange-500' : 'text-red-600';

  return (
    <div className="p-4 md:p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
            Dashboard
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-4 md:mt-0">
          {/* Client filter (top row) */}
          {stats?.filter_options?.clients?.length > 0 && (
            <select
              value={selectedClient || 'all'}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedClient(v === 'all' ? '' : v);
                setSelectedFacility('');
              }}
              aria-label="Client filter"
              className="text-sm border border-slate-200 dark:border-slate-600 rounded-md px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 max-w-[180px] truncate"
              data-testid="dashboard-client-filter"
            >
              <option value="all">All Clients</option>
              {stats.filter_options.clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          
          {/* Facility filter (top row) */}
          {stats?.filter_options?.facilities?.length > 0 && (
            <select
              value={selectedFacility || 'all'}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedFacility(v === 'all' ? '' : v);
              }}
              aria-label="Facility filter"
              className="text-sm border border-slate-200 dark:border-slate-600 rounded-md px-3 py-1.5 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 max-w-[200px] truncate"
              data-testid="dashboard-facility-filter"
            >
              <option value="all">All Facilities</option>
              {(selectedClient
                ? stats.filter_options.facilities.filter(f => f.client_id === selectedClient)
                : stats.filter_options.facilities
              ).map(f => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          )}
          
          {/* Clear filters (only when active) */}
          {(selectedClient || selectedFacility) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelectedClient(''); setSelectedFacility(''); }}
              className="text-slate-500 hover:text-slate-700 dark:text-slate-400 h-8 px-2"
              data-testid="dashboard-clear-filters-btn"
            >
              <X size={14} />
            </Button>
          )}
          
          {/* Module Filter */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {[
              { key: 'all', label: 'All' },
              { key: 'fca', label: 'Assessment' },
              { key: 'iwms', label: 'Work Mgmt' }
            ].map(mod => (
              <button
                key={mod.key}
                onClick={() => setSelectedModule(mod.key)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  selectedModule === mod.key
                    ? 'bg-white dark:bg-slate-700 text-teal-600 shadow-sm'
                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                }`}
              >
                {mod.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportDashboardPdf}
            disabled={exportingPdf}
            data-testid="dashboard-export-pdf-btn"
          >
            {exportingPdf
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileDown className="w-4 h-4 mr-2" />}
            Export PDF
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => fetchStats(true)}
            disabled={refreshing}
            className="h-8 w-8"
            aria-label="Refresh"
            title="Refresh"
            data-testid="dashboard-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* (old Client/Facility filter row removed — now in the top action bar) */}

      {/* Dashboard scope: combined data transparency + operational toggle.
          Left side narrates which assets feed today's KPIs (the breakdown
          updates with the toggle); right side lets the user flip the scope. */}
      {stats?.assets && stats.assets.total > 0 && (
        <TooltipProvider>
          <div className="flex flex-col md:flex-row gap-3 mb-4" data-testid="dashboard-scope-bar">
            {/* Left half: KPI scope + include drafts toggle */}
            <div className="md:w-1/2 flex items-center justify-between gap-3 px-3 py-1.5 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 rounded-lg text-sm">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5 cursor-help min-w-0" data-testid="data-transparency-indicator">
                    <span className="text-slate-700 dark:text-slate-300 truncate">
                      KPIs reflect{' '}
                      <span className="font-semibold text-emerald-600">
                        {(stats.assets.approved || 0).toLocaleString()} approved
                      </span>
                      {(stats.assets.locked || 0) > 0 && (
                        <>
                          {' + '}
                          <span className="font-semibold text-amber-600">
                            {stats.assets.locked.toLocaleString()} pending review
                          </span>
                        </>
                      )}
                      {includeDrafts && (stats.assets.draft || 0) > 0 && (
                        <>
                          {' + '}
                          <span className="font-semibold text-slate-500">
                            {stats.assets.draft.toLocaleString()} draft
                          </span>
                        </>
                      )}
                      {' '}assets
                      {!includeDrafts && (stats.assets.draft || 0) > 0 && (
                        <span className="text-slate-400 ml-1">
                          ({stats.assets.draft.toLocaleString()} draft hidden)
                        </span>
                      )}
                    </span>
                    <HelpCircle size={14} className="text-slate-400 shrink-0" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm bg-slate-900 text-white p-3">
                  <p className="font-semibold mb-2">Asset scope</p>
                  <div className="text-xs space-y-1">
                    <p>
                      <span className="text-emerald-400">Approved ({(stats.assets.approved || 0).toLocaleString()}):</span>{' '}
                      SME-verified, IWMS-ready
                    </p>
                    <p>
                      <span className="text-amber-400">Pending review ({(stats.assets.locked || 0).toLocaleString()}):</span>{' '}
                      Submitted, awaiting SME
                    </p>
                    <p>
                      <span className="text-slate-400">Draft ({(stats.assets.draft || 0).toLocaleString()}):</span>{' '}
                      Work in progress — {includeDrafts ? 'currently included' : 'currently excluded'}
                    </p>
                    {(stats.assets.rejected || 0) > 0 && (
                      <p>
                        <span className="text-red-400">Rejected ({stats.assets.rejected.toLocaleString()}):</span>{' '}
                        Needs revision (never counted)
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-700">
                    Toggle &ldquo;Include draft assets&rdquo; to flip between the operational
                    portfolio and the full inventory snapshot.
                  </p>
                </TooltipContent>
              </Tooltip>

              {/* Scope toggle — operational vs. full inventory */}
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500 dark:text-slate-400 hidden sm:inline">
                  Include draft assets
                </span>
                <Switch
                  checked={includeDrafts}
                  onCheckedChange={handleToggleIncludeDrafts}
                  disabled={savingIncludeDrafts}
                  data-testid="include-drafts-toggle"
                  aria-label="Include draft assets in dashboard KPIs"
                />
              </div>
            </div>

            {/* Right half: Plant Replacement Value (above WO Costs) + FCI (above PM Compliance) */}
            <div
              className="md:w-1/2 grid grid-cols-2 items-center px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg"
              data-testid="dashboard-key-metrics-strip"
            >
              <button
                type="button"
                onClick={() => navigate('/facilities')}
                className="flex items-baseline gap-2 hover:text-teal-600 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 rounded justify-self-center"
                data-testid="strip-portfolio-value"
              >
                <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">Plant Replacement Value</span>
                <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                  {formatCurrency(stats?.financials?.total_replacement_value || 0)}
                </span>
              </button>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-baseline gap-2 cursor-help justify-self-center" data-testid="strip-fci">
                    <span className="flex items-center gap-1 text-sm font-semibold text-slate-600 dark:text-slate-300">
                      FCI
                      <HelpCircle size={11} className="text-slate-400" />
                    </span>
                    <span className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
                      {formatFCI(stats?.financials?.fci_percent)}%
                    </span>
                    <Badge
                      variant={stats?.financials?.fci_rating === 'Good' ? 'default' : 'destructive'}
                      className="text-[10px] py-0 px-1.5 h-4"
                    >
                      {stats?.financials?.fci_rating || 'N/A'}
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs bg-slate-900 text-white p-3">
                  <p className="font-semibold mb-1">Facility Condition Index (FCI)</p>
                  <p className="text-xs text-slate-300 mb-2">
                    FCI = Deferred Maintenance ÷ Asset Replacement Value
                  </p>
                  <p className="text-xs text-slate-400 mb-2 italic">
                    Based on assessed asset values from FCA, not building value
                  </p>
                  <div className="text-xs space-y-1 border-t border-slate-700 pt-2 mt-2">
                    <p><span className="text-emerald-400">Good:</span> ≤5%</p>
                    <p><span className="text-amber-400">Fair:</span> 5-10%</p>
                    <p><span className="text-orange-400">Poor:</span> 10-30%</p>
                    <p><span className="text-red-400">Critical:</span> &gt;30%</p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </TooltipProvider>
      )}

      {/* Key Metrics combined into the scope bar above (Portfolio + FCI on the
          right half). Deferred Maint. and Expiring Warranties were removed —
          they're already represented in dedicated cards / chips below. */}

      {/* v1.3.37.63.1 — SecurityAtAGlanceTile relocated to
          System Admin → Audit Log tab.  Its auth-event posture felt
          out of place next to asset-KPI cards and its drill-through
          already lands in the Audit Log, so it now lives at the top
          of that tab. */}

      {/* v1.3.37.137 — Team Kickbacks tile relocated (compact) to
          System Admin → AI Quality. */}

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {/* Replacement Value by System (UNIFORMAT Level 2) */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-7">
            <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                            Replacement Value by System
            </h3>
            <div className="space-y-2">
              {Object.entries(stats?.financials?.cost_by_uniformat || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 7)
                .map(([code, cost], idx) => {
                  const colors = ['bg-teal-500', 'bg-sky-500', 'bg-purple-500', 'bg-indigo-500', 'bg-pink-500'];
                  const maxUniformat = Math.max(...Object.values(stats?.financials?.cost_by_uniformat || {}), 1);
                  const name = UNIFORMAT_LABELS[code];
                  return (
                    <HorizontalBar 
                      key={code}
                      label={name ? `${code} - ${name}` : code}
                      value={cost} 
                      maxValue={maxUniformat} 
                      color={colors[idx % colors.length]}
                      prefix="$"
                    />
                  );
                })}
            </div>
          </Card>
        )}

        {/* Deferred Maintenance by Priority */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-2" data-testid="deferred-maintenance-card">
            {(() => {
              const costs = stats?.financials?.cost_by_priority || {};
              const counts = stats?.financials?.count_by_priority || {};
              const total = stats?.financials?.total_deferred_maintenance || 0;
              const actionableCost = (costs.critical || 0) + (costs.high || 0);
              const actionableCount = (counts.critical || 0) + (counts.high || 0);
              const actionablePct = total > 0 ? (actionableCost / total) * 100 : 0;

              const headlineClass = actionablePct >= 50
                ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                : actionablePct >= 20
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
              const headlineSymbol = actionablePct >= 50 ? '⚠' : actionablePct >= 20 ? '•' : '✓';
              const totalCount =
                (counts.critical || 0) + (counts.high || 0) + (counts.medium || 0) + (counts.low || 0);

              const maxCostLocal = Math.max(
                costs.critical || 0,
                costs.high || 0,
                costs.medium || 0,
                costs.low || 0,
                1,
              );

              const rows = [
                { key: 'critical', label: 'Critical', barClass: 'bg-rose-500' },
                { key: 'high',     label: 'High',     barClass: 'bg-amber-500' },
                { key: 'medium',   label: 'Medium',   barClass: 'bg-sky-500' },
                { key: 'low',      label: 'Low',      barClass: 'bg-slate-400' },
              ];

              return (
                <>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                    Deferred Maint. by Priority
                  </h3>

                  {/* Headline KPI */}
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      Total backlog
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-400">
                      {totalCount} item{totalCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums mb-2">
                    {formatCurrency(total)}
                  </div>
                  <div
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium border rounded-full px-2 py-0.5 mb-4 ${headlineClass}`}
                    data-testid="deferred-actionable-chip"
                  >
                    <span aria-hidden="true">{headlineSymbol}</span>
                    <span>
                      {formatCurrency(actionableCost)} ({actionablePct.toFixed(0)}%) actionable now · {actionableCount} item{actionableCount === 1 ? '' : 's'}
                    </span>
                  </div>

                  {/* Per-priority rows */}
                  <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    {rows.map(({ key, label, barClass }) => {
                      const cost = costs[key] || 0;
                      const count = counts[key] || 0;
                      const pct = (cost / maxCostLocal) * 100;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => navigateWithBack('/assessments', { priorityFilter: key })}
                          className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
                          data-testid={`deferred-row-${key}`}
                        >
                          <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="text-slate-700 dark:text-slate-200 font-medium group-hover:text-slate-900 dark:group-hover:text-white">
                              {label}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-full px-1.5 py-0.5 tabular-nums">
                                {count} item{count === 1 ? '' : 's'}
                              </span>
                              <span className="font-semibold text-slate-900 dark:text-white tabular-nums w-16 text-right">
                                {formatCurrency(cost)}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                            <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </Card>
        )}

        {/* Deficiencies by Priority */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-8">
            <h3 className="font-bold text-slate-900 dark:text-white mb-3">
                            Assessment Status
            </h3>
            <div className="space-y-3">
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'draft' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-slate-400" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Draft</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.draft_assessments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'in_progress' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-sky-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">In Progress</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.in_progress_assessments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'pending_review' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Pending Review</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.pending_review_assessments || 0}</span>
              </div>
              <div 
                className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded-lg cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-600"
                onClick={() => navigateWithBack('/assessments', { statusFilter: 'approved' })}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-sm text-slate-600 dark:text-slate-300">Approved</span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-white">{stats?.fca?.approved_assessments || 0}</span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Total Assessments</span>
                <span className="font-bold text-slate-900 dark:text-white">{stats?.fca?.total_assessments || 0}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Work Order Trends — Option A: Combined overlay with shaded gap */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-5" data-testid="work-order-trends-card">
            {(() => {
              const createdSeries = stats?.trends?.work_orders_created || [];
              const completedSeries = stats?.trends?.work_orders_completed || [];
              const totalCreated = createdSeries.reduce((sum, d) => sum + d.count, 0);
              const totalCompleted = completedSeries.reduce((sum, d) => sum + d.count, 0);
              const backlog = totalCreated - totalCompleted;
              // Trend direction: compare backlog delta of the latter half vs the earlier half
              const n = Math.min(createdSeries.length, completedSeries.length);
              const half = Math.floor(n / 2);
              const firstHalfDelta = createdSeries.slice(0, half).reduce((s, d) => s + d.count, 0)
                                  - completedSeries.slice(0, half).reduce((s, d) => s + d.count, 0);
              const secondHalfDelta = createdSeries.slice(half).reduce((s, d) => s + d.count, 0)
                                   - completedSeries.slice(half).reduce((s, d) => s + d.count, 0);
              const direction = backlog <= 0
                ? 'cleared'
                : (secondHalfDelta > firstHalfDelta ? 'widening' : (secondHalfDelta < firstHalfDelta ? 'narrowing' : 'steady'));
              const monthsCovered = n;
              const chipClass = backlog <= 0
                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                : direction === 'widening'
                  ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                  : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800';
              const chipSymbol = backlog <= 0 ? '✓' : direction === 'widening' ? '⚠' : '•';
              const chipText = backlog <= 0
                ? `Backlog cleared · ${Math.abs(backlog)} ahead over ${monthsCovered} months`
                : `Backlog +${backlog} over ${monthsCovered} months · ${direction}`;
              return (
                <>
                  <div className="flex items-center justify-between mb-2 gap-2">
                    <h3 className="font-bold text-slate-900 dark:text-white whitespace-nowrap">
                      Work Order Trends
                    </h3>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] mb-2">
                    <span className="inline-flex items-center gap-1 text-sky-600 font-medium">
                      <span className="inline-block w-2 h-2 rounded-full bg-sky-500" />
                      Created · {totalCreated}
                    </span>
                    <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                      <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                      Completed · {totalCompleted}
                    </span>
                  </div>
                  <WorkOrderTrendOverlay
                    created={createdSeries}
                    completed={completedSeries}
                    height={80}
                  />
                  <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'rgba(16, 185, 129, 0.35)' }} />
                      Keeping up
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="inline-block w-3 h-2 rounded-sm" style={{ background: 'rgba(239, 68, 68, 0.35)' }} />
                      Falling behind
                    </span>
                  </div>
                  <div
                    className={`mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium border rounded-full px-2 py-0.5 ${chipClass}`}
                    data-testid="work-order-backlog-chip"
                  >
                    <span aria-hidden="true">{chipSymbol}</span>
                    <span>{chipText}</span>
                  </div>
                </>
              );
            })()}
          </Card>
        )}

        {/* Work Orders by Type — Stacked status bars + avg days-to-close */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-6" data-testid="work-orders-by-type-card">
            <h3 className="font-bold text-slate-900 dark:text-white mb-1 whitespace-nowrap">Work Orders by Type</h3>
            <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400 mb-3">
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-sm bg-slate-400" style={{ opacity: 0.35 }} />
                Open
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-sm bg-slate-400" style={{ opacity: 0.65 }} />
                In&nbsp;progress
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-sm bg-slate-400" />
                Completed
              </span>
            </div>
            <div className="space-y-2">
              {(() => {
                const counts = stats?.iwms?.work_orders_by_type || {};
                const breakdown = stats?.iwms?.work_orders_by_type_breakdown || {};
                // Order rows by total volume descending; hide zero-volume rows
                const rows = Object.entries(counts)
                  .filter(([, total]) => (total || 0) > 0)
                  .sort((a, b) => b[1] - a[1]);
                if (rows.length === 0) {
                  return <div className="text-xs text-slate-400">No work orders recorded</div>;
                }
                const maxWO = Math.max(...rows.map(([, v]) => v), 1);
                return rows.map(([type, total]) => {
                  const breakdownRow = breakdown[type] || { open: 0, in_progress: 0, completed: 0, avg_days_to_close: null };
                  const open = breakdownRow.open || 0;
                  const inProgress = breakdownRow.in_progress || 0;
                  const completed = breakdownRow.completed || 0;
                  const sumStatus = open + inProgress + completed;
                  // Width of the WHOLE row relative to the largest type
                  const rowWidthPct = (total / maxWO) * 100;
                  // Segments WITHIN the row (percentages of total)
                  const openPct = sumStatus ? (open / sumStatus) * 100 : 0;
                  const ipPct = sumStatus ? (inProgress / sumStatus) * 100 : 0;
                  const cmpPct = sumStatus ? (completed / sumStatus) * 100 : 0;
                  const displayLabel = woTypeDisplayLabel(type);
                  const colorBg = woTypeColor(displayLabel);
                  const avgDays = breakdownRow.avg_days_to_close;
                  const sla = evaluateSla(displayLabel, avgDays);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => navigateWithBack('/work-orders', { typeFilter: type })}
                      className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
                      data-testid={`wo-type-row-${type}`}
                    >
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="text-slate-700 dark:text-slate-200 font-medium group-hover:text-slate-900 dark:group-hover:text-white">
                          {displayLabel}
                        </span>
                        <div className="flex items-center gap-2">
                          {avgDays !== null && avgDays !== undefined && (
                            <span
                              className={`inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-1.5 py-0.5 ${sla.chipClass}`}
                              title={sla.tooltip}
                              data-testid={`wo-type-sla-${type}`}
                            >
                              {sla.symbol && <span aria-hidden="true">{sla.symbol}</span>}
                              {!sla.symbol && <i className="far fa-clock text-[9px]" aria-hidden="true" />}
                              {avgDays}d avg
                              {sla.target && <span className="opacity-60 hidden xl:inline">· goal {sla.target}d</span>}
                            </span>
                          )}
                          <span className="font-semibold text-slate-900 dark:text-white tabular-nums">{total}</span>
                        </div>
                      </div>
                      {/* Status-stacked bar — type-color tinted segments */}
                      <div className="relative h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div className="flex h-full" style={{ width: `${rowWidthPct}%` }}>
                          {/* Open — type color at 35% opacity */}
                          <div
                            className={`${colorBg} h-full`}
                            style={{ width: `${openPct}%`, opacity: 0.35 }}
                            title={`Open: ${open}`}
                          />
                          {/* In progress — type color at 65% opacity */}
                          <div
                            className={`${colorBg} h-full`}
                            style={{ width: `${ipPct}%`, opacity: 0.65 }}
                            title={`In progress: ${inProgress}`}
                          />
                          {/* Completed — full type color */}
                          <div
                            className={`${colorBg} h-full`}
                            style={{ width: `${cmpPct}%` }}
                            title={`Completed: ${completed}`}
                          />
                        </div>
                      </div>
                    </button>
                  );
                });
              })()}
            </div>
          </Card>
        )}

        {/* Work Order Costs — Budget health + top spend by type */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-3" data-testid="work-order-costs-card">
            {(() => {
              const est = stats?.financials?.work_order_costs?.estimated || 0;
              const actual = stats?.financials?.work_order_costs?.actual || 0;
              const variance = actual - est; // positive => over budget
              const utilization = est > 0 ? Math.min(100, (actual / est) * 100) : 0;
              const overBudget = variance > 0;
              const overshootPct = est > 0 && overBudget ? ((variance / est) * 100) : 0;

              const chipClass = overBudget
                ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                : utilization >= 80
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
              const chipSymbol = overBudget ? '⚠' : utilization >= 80 ? '•' : '✓';
              const chipText = overBudget
                ? `Over budget by ${formatCurrency(variance)} · ${overshootPct.toFixed(0)}% overshoot`
                : `Under budget by ${formatCurrency(Math.abs(variance))} · ${utilization.toFixed(0)}% utilized`;
              const windowLabel = costWindow === '30d' ? 'Last 30 days' : `${new Date().getFullYear()} YTD`;

              const byType = stats?.financials?.work_order_costs?.by_type || {};
              const typeEntries = Object.entries(byType)
                .filter(([, v]) => (v || 0) > 0)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5);
              const maxTypeCost = typeEntries.length ? Math.max(...typeEntries.map(([, v]) => v)) : 1;

              return (
                <>
                  <div className="flex items-center justify-between mb-3 gap-2">
                    <h3 className="font-bold text-slate-900 dark:text-white whitespace-nowrap">Work Order Costs</h3>
                    <div
                      className="inline-flex items-center rounded-md border border-slate-200 dark:border-slate-600 overflow-hidden text-[10px] font-medium"
                      data-testid="wo-costs-window-toggle"
                    >
                      <button
                        type="button"
                        onClick={() => setCostWindow('ytd')}
                        className={`px-2 py-0.5 transition-colors ${costWindow === 'ytd' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-transparent text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        data-testid="wo-costs-window-ytd"
                      >
                        YTD
                      </button>
                      <button
                        type="button"
                        onClick={() => setCostWindow('30d')}
                        className={`px-2 py-0.5 transition-colors ${costWindow === '30d' ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900' : 'bg-transparent text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
                        data-testid="wo-costs-window-30d"
                      >
                        30d
                      </button>
                    </div>
                  </div>

                  {/* Top: Budget bar */}
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-[11px] text-slate-500 dark:text-slate-300">
                      Actual <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(actual)}</span>
                    </span>
                    <span className="text-[11px] text-slate-500 dark:text-slate-300">
                      Budget <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(est)}</span>
                    </span>
                  </div>
                  <div className="relative h-3 w-full rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                    {/* Filled bar — capped visually at 100%; overshoot rendered as separate strip */}
                    <div
                      className={`absolute left-0 top-0 h-full ${overBudget ? 'bg-rose-500' : utilization >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${utilization}%` }}
                    />
                    {/* Overshoot stripe on top — diagonal pattern to flag over-budget */}
                    {overBudget && (
                      <div
                        className="absolute top-0 h-full opacity-70"
                        style={{
                          left: '100%',
                          width: `${Math.min(20, overshootPct)}%`,
                          background: 'repeating-linear-gradient(45deg, #be123c, #be123c 4px, #f43f5e 4px, #f43f5e 8px)',
                          transform: 'translateX(-100%)',
                        }}
                      />
                    )}
                  </div>

                  {/* Variance chip */}
                  <div
                    className={`mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium border rounded-full px-2 py-0.5 ${chipClass}`}
                    data-testid="work-order-budget-chip"
                  >
                    <span aria-hidden="true">{chipSymbol}</span>
                    <span>{chipText}</span>
                  </div>
                  <div className="mt-1 text-[10px] text-slate-400 dark:text-slate-500">{windowLabel}</div>

                  {/* Bottom: Cost by type — top 3 */}
                  <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
                      Top Spend by Type
                    </div>
                    {typeEntries.length === 0 ? (
                      <div className="text-xs text-slate-400">No spend recorded yet</div>
                    ) : (
                      <div className="space-y-2">
                        {typeEntries.map(([type, value]) => {
                          const pct = (value / maxTypeCost) * 100;
                          const displayLabel = woTypeDisplayLabel(type);
                          return (
                            <div key={type}>
                              <div className="flex items-center justify-between text-[11px] mb-1">
                                <span className="text-slate-700 dark:text-slate-200 font-medium" title={displayLabel}>
                                  {displayLabel}
                                </span>
                                <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
                                  {formatCurrency(value)}
                                </span>
                              </div>
                              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                                <div className={`h-full ${woTypeColor(displayLabel)}`} style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </Card>
        )}

        {/* Asset Lifecycle — Remaining useful life buckets + EOL headline */}
        {(selectedModule === 'all' || selectedModule === 'fca') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-1" data-testid="asset-lifecycle-card">
            {(() => {
              const buckets = stats?.assets?.lifecycle_buckets || {};
              const totalWithData = stats?.assets?.lifecycle_total_with_data || 0;
              const eolCount = buckets?.eol?.count || 0;
              const eolCost = buckets?.eol?.sum_cost || 0;
              const eolPct = totalWithData > 0 ? (eolCount / totalWithData) * 100 : 0;

              const headlineClass = eolPct >= 30
                ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                : eolPct >= 15
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
              const headlineSymbol = eolPct >= 30 ? '⚠' : eolPct >= 15 ? '•' : '✓';

              const rows = [
                { key: 'eol',     label: 'End-of-life (0y)',  barClass: 'bg-rose-500' },
                { key: 'aging',   label: 'Aging (1–5y)',      barClass: 'bg-amber-500' },
                { key: 'mid_life',label: 'Mid-life (5–10y)',  barClass: 'bg-sky-500' },
                { key: 'healthy', label: 'Healthy (10–15y)',  barClass: 'bg-emerald-500' },
                { key: 'new',     label: 'New (15y+)',        barClass: 'bg-emerald-700' },
              ];
              const maxCount = Math.max(...rows.map(r => buckets?.[r.key]?.count || 0), 1);

              return (
                <>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-3">Asset Lifecycle</h3>

                  {/* Headline KPI */}
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">
                    Past end-of-life
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums mb-2">
                    {eolCount.toLocaleString()} <span className="text-base font-normal text-slate-500 dark:text-slate-400">of {totalWithData.toLocaleString()} assets</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigateWithBack('/assets')}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium border rounded-full px-2 py-0.5 mb-4 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-sky-500 ${headlineClass}`}
                    data-testid="asset-lifecycle-eol-chip"
                    title="Click to view assets"
                  >
                    <span aria-hidden="true">{headlineSymbol}</span>
                    <span>
                      {eolPct.toFixed(0)}% past EOL · {formatCurrency(eolCost)} at-risk
                    </span>
                  </button>

                  {/* Per-bucket rows */}
                  <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    {rows.map(({ key, label, barClass }) => {
                      const bucket = buckets[key] || { count: 0, sum_cost: 0 };
                      const pct = (bucket.count / maxCount) * 100;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => navigateWithBack('/assets')}
                          className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
                          data-testid={`asset-lifecycle-row-${key}`}
                        >
                          <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="text-slate-700 dark:text-slate-200 font-medium group-hover:text-slate-900 dark:group-hover:text-white">
                              {label}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700 rounded-full px-1.5 py-0.5 tabular-nums">
                                {bucket.count.toLocaleString()} asset{bucket.count === 1 ? '' : 's'}
                              </span>
                              <span className="font-semibold text-slate-900 dark:text-white tabular-nums w-16 text-right">
                                {formatCurrency(bucket.sum_cost || 0)}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                            <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </Card>
        )}

        {/* PM Compliance & Coverage */}
        {(selectedModule === 'all' || selectedModule === 'iwms') && (
          <Card className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 lg:order-4" data-testid="pm-compliance-card">
            {(() => {
              const buckets = stats?.iwms?.pm_compliance_buckets || {};
              const overdue = buckets.overdue || 0;
              const dueWeek = buckets.due_this_week || 0;
              const due30 = buckets.due_30d || 0;
              const due90 = buckets.due_90d || 0;
              const onTrack = buckets.on_track || 0;
              const unscheduled = buckets.unscheduled || 0;
              const totalActive = overdue + dueWeek + due30 + due90 + onTrack + unscheduled;
              const urgent = overdue + dueWeek; // "needs attention this week"
              const compliancePct = totalActive > 0 ? ((totalActive - overdue) / totalActive) * 100 : 100;

              const headlineClass = compliancePct < 70
                ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                : compliancePct < 90
                  ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                  : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800';
              const headlineSymbol = compliancePct < 70 ? '⚠' : compliancePct < 90 ? '•' : '✓';

              const rows = [
                { key: 'overdue',       label: 'Overdue',          value: overdue,    barClass: 'bg-rose-500' },
                { key: 'due_this_week', label: 'Due this week',    value: dueWeek,    barClass: 'bg-amber-500' },
                { key: 'due_30d',       label: 'Due in 30 days',   value: due30,      barClass: 'bg-sky-500' },
                { key: 'due_90d',       label: 'Due in 90 days',   value: due90,      barClass: 'bg-emerald-500' },
                { key: 'on_track',      label: 'On track (>90 days)', value: onTrack, barClass: 'bg-emerald-700' },
              ];
              const maxRow = Math.max(...rows.map(r => r.value), 1);

              return (
                <>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-3">PM Compliance</h3>

                  {/* Headline KPI */}
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-0.5">
                    Compliance rate
                  </div>
                  <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums mb-2">
                    {compliancePct.toFixed(0)}% <span className="text-base font-normal text-slate-500 dark:text-slate-400">of {totalActive.toLocaleString()} active PMs</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigateWithBack('/pm-schedules')}
                    className={`inline-flex items-center gap-1.5 text-[11px] font-medium border rounded-full px-2 py-0.5 mb-4 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-sky-500 ${headlineClass}`}
                    data-testid="pm-compliance-urgent-chip"
                    title="Click to view PM schedule"
                  >
                    <span aria-hidden="true">{headlineSymbol}</span>
                    <span>
                      {urgent} PM{urgent === 1 ? '' : 's'} need attention this week
                    </span>
                  </button>

                  {/* Per-bucket rows */}
                  <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                    {rows.map(({ key, label, value, barClass }) => {
                      const pct = (value / maxRow) * 100;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => navigateWithBack('/pm-schedules')}
                          className="w-full text-left group focus:outline-none focus:ring-2 focus:ring-sky-500 rounded"
                          data-testid={`pm-compliance-row-${key}`}
                        >
                          <div className="flex items-center justify-between text-[11px] mb-1">
                            <span className="text-slate-700 dark:text-slate-200 font-medium group-hover:text-slate-900 dark:group-hover:text-white">
                              {label}
                            </span>
                            <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
                              {value.toLocaleString()}
                            </span>
                          </div>
                          <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                            <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
                          </div>
                        </button>
                      );
                    })}
                    {unscheduled > 0 && (
                      <div className="text-[10px] text-slate-400 dark:text-slate-500 pt-1">
                        {unscheduled} active PM{unscheduled === 1 ? '' : 's'} have no due date set
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </Card>
        )}
      </div>

      {/* 20-Year Capital Replacement Forecast */}
      <Card className="mt-4 p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700" data-testid="capital-forecast-card">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">20-Year Capital Replacement Forecast</h3>
          {(selectedClient || selectedFacility) && (
            <Badge variant="secondary" className="ml-2">Filtered</Badge>
          )}
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-300 mb-4">
          Projected replacement costs based on asset useful life analysis
        </p>
        <ForecastBarChart data={stats?.replacement_forecast || []} height={220} />
        {stats?.replacement_forecast && stats.replacement_forecast.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-teal-500"></div>
                <span className="text-slate-600 dark:text-slate-300">Current Year</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded bg-sky-400"></div>
                <span className="text-slate-600 dark:text-slate-300">Future Years</span>
              </div>
              <div className="ml-auto text-slate-500">
                Total 20-Year Cost: <span className="font-semibold text-slate-900 dark:text-white">
                  {formatCurrency(stats.replacement_forecast.reduce((sum, item) => sum + (item.cost || 0), 0))}
                </span>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Last Updated */}
      <div className="mt-4 text-center text-xs text-slate-400">
        Last updated: {stats?.generated_at ? new Date(stats.generated_at).toLocaleString() : 'N/A'}
      </div>
    </div>
  );
};

export default DashboardPage;
